import { useState, useEffect } from "react";
import images from "../../../../assets/Icons/Icons";
import { LEVELS, SOCIAL_COMPETENCIES } from "./SocialEmoMockdata";
import "./SocialCard.scss";
import InfoTooltip from "./components/InfoToolTip";
import useCaptureSelPulse, {
  CompetencyType,
} from "../../../../hooks/useCaptureSelPulse";
import useFetchSelScores from "../../../../hooks/useFetchSelScores";
import { trackEvent } from "../../../../mixpanel";

const LEVEL_TO_SCORE: Record<string, number> = {
  Early: 1,
  Developing: 2,
  Understanding: 3,
  Mature: 4,
};
const SCORE_TO_LEVEL: Record<number, string> = {
  1: "Early",
  2: "Developing",
  3: "Understanding",
  4: "Mature",
};

// Utility function to convert "Self Management" -> "SELF_MANAGEMENT"
const toConstantCase = (str: string) => str.replace(/\s+/g, "_").toUpperCase();

const SocialEmotionalCard = ({userId,schoolHash,classId}:{userId:string,schoolHash:string,classId:string}) => {
  const [isEdit, setIsEdit] = useState(false);
  const { captureSel, loading } = useCaptureSelPulse();

  // API params
  // const userId = "04780448-a0f1-7084-4ef7-c74e52913ec1";
  // const schoolHash =
  //   "ae1547da394ae9fa796f752d096a087f40455348f9ddafac11351c20ba746249";
  // const classId = "third";

  // Fetch API data
  const { data, loading: fetchLoading } = useFetchSelScores(
    schoolHash,
    classId,
    userId
  );

  const [values, setValues] = useState<Record<string, string>>({
    selfAwareness: "Early",
    selfManagement: "Developing",
    socialAwareness: "Understanding",
    relationshipSkills: "Mature",
    responsibleDecisionMaking: "Early",
  });

  // Set dropdown values from API data
  // Map API competency keys to local keys
  useEffect(() => {
    if (data?.competencies) {
      const newValues: Record<string, string> = { ...values };
      for (const item of SOCIAL_COMPETENCIES) {
        const apiKey = toConstantCase(item.label);
        const comp = data.competencies[apiKey as CompetencyType];
        if (comp && typeof comp.latest_adult_score === "number") {
          // fallback to 1 if not mapped
          newValues[item.key] =
            SCORE_TO_LEVEL[comp.latest_adult_score] || "Early";
        }
      }
      setValues(newValues);
    }
    // eslint-disable-next-line
  }, [data]);

  const handleChange = (key: string, value: string) => {
    setValues((prev) => ({ ...prev, [key]: value }));
  };

  // Save all dropdown values as a single API call with pulses array
  const handleSave = async () => {
    try {
      const pulses = SOCIAL_COMPETENCIES.map((item: any) => ({
        competency: toConstantCase(item.label) as CompetencyType,
        S: LEVEL_TO_SCORE[values[item.key]],
        W: 0.7,
        I: 1.0,
        E: 1.0,
      }));

      await captureSel({
        userId,
        schoolHash,
        classId,
        source_type: "Adult",
        pulses,
      });

      // Track SEL maturity edit
      trackEvent("SEL_MATURITY_EDITED");

      setIsEdit(false);
    } catch (error) {
      console.error("Error saving competencies", error);
    }
  };

  return (
    <div className="performance-card">
      <div className="card-header">
        <h3>Social-emotional aptitude</h3>

        {!isEdit ? (
          <button className="edit-btn" onClick={() => setIsEdit(true)} disabled={!userId}>
            <img src={images.pencil} width={18} /> Edit
          </button>
        ) : (
          <div className="action-buttons">
            <button onClick={() => setIsEdit(false)}>✕</button>
            <button onClick={handleSave} disabled={loading || !userId}>
              <img src={images.checkIcon} style={{marginTop:4}} width={20} height={20} />
            </button>
          </div>
        )}
      </div>

      <div className="sel-card-body">
        {(fetchLoading ? SOCIAL_COMPETENCIES : SOCIAL_COMPETENCIES)?.map(
          (item: any) => {
            const selectedLevel = values[item.key];

            return (
              <div className="sel-row" key={item.key}>
                <div className="sel-row-space">
                  <span className="sel-label" style={{ color: item.color }}>
                    {item.label.toUpperCase()}
                  </span>
                  <InfoTooltip
                    details={item.details}
                    selectedLevel={selectedLevel}
                  />
                </div>
                {!isEdit ? (
                  <span className="sel-value">{selectedLevel}</span>
                ) : (
                  <select
                    value={selectedLevel}
                    onChange={(e) => handleChange(item.key, e.target.value)}
                    className="sel-level-select"
                    disabled={loading}
                  >
                    {LEVELS.map((lvl) => (
                      <option key={lvl} value={lvl}>
                        {lvl}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            );
          }
        )}
      </div>
    </div>
  );
};

export default SocialEmotionalCard;
